/* platform/Plans — Plan Management. SA-only.
   List, create, edit, clone, preview, compare, archive, restore, set-default, version history.
   Entitlements managed via searchable feature tree (featureRegistry). */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { StatCard, PageHead, Th, TenancyModal } from './_ui';
import * as planApi from '../api/plan.api.js';
import {
  FEATURE_REGISTRY, getAllLeafKeys, TOTAL_FEATURE_KEYS,
  PLAN_FEATURE_DEFAULTS, resolveDependencies, normalizeFeatureKeys,
} from './featureRegistry.js';

// ── Constants ─────────────────────────────────────────────────────────────────

const TIER_ORDER  = ['STARTER', 'PROFESSIONAL', 'BUSINESS', 'ENTERPRISE', 'CUSTOM'];
const TIER_LABEL  = { STARTER: 'Starter', PROFESSIONAL: 'Professional', BUSINESS: 'Business', ENTERPRISE: 'Enterprise', CUSTOM: 'Custom' };
const TIER_TONE   = { STARTER: 'gray', PROFESSIONAL: 'blue', BUSINESS: 'green', ENTERPRISE: 'purple', CUSTOM: 'amber' };
const STATUS_TONE = { DRAFT: 'amber', ACTIVE: 'green', ARCHIVED: 'gray' };
const CURRENCY_OPTIONS = ['INR', 'USD', 'AED', 'SGD', 'GBP', 'EUR'];

const PAGE_SIZE = 20;

const EMPTY_FORM = {
  code: '', name: '', displayName: '', description: '', tier: 'STARTER',
  featureKeys: [], isPublic: true,
  priceMonthly: '', priceAnnual: '', currency: 'INR', trialDays: '14',
  maxWorkspaces: '', maxStorageGb: '', maxAgents: '', maxBots: '',
  maxApiCallsPerDay: '', maxMessagesPerMonth: '',
  sortOrder: 0,
};

const intOrNull   = (v) => (v === '' || v == null) ? null : parseInt(v, 10);
const floatOrNull = (v) => (v === '' || v == null) ? null : parseFloat(v);

const fmtPrice = (val, currency) => {
  if (val == null) return 'Custom';
  const sym = { INR: '₹', USD: '$', AED: 'AED ', SGD: 'S$', GBP: '£', EUR: '€' }[currency] || currency + ' ';
  return sym + val.toLocaleString('en-IN');
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';

// ── FeatureTree helpers ───────────────────────────────────────────────────────

const leafsOf = (node) => {
  if (!node.children) return [node.key];
  return node.children.flatMap(leafsOf);
};

const nodeMatches = (node, lq) => {
  if (!lq) return true;
  if (node.label.toLowerCase().includes(lq) || node.key.includes(lq)) return true;
  return (node.children || []).some(c => nodeMatches(c, lq));
};

const collectGroupKeys = (nodes) => {
  const keys = [];
  const visit = (node) => {
    if (node.children) {
      keys.push(node.key);
      node.children.forEach(visit);
    }
  };
  nodes.forEach(visit);
  return keys;
};

// ── TreeNode ──────────────────────────────────────────────────────────────────

function TreeNode({ node, depth, sel, q, open, onToggleLeaf, onToggleGroup, onToggleOpen, readOnly }) {
  const lq     = q.toLowerCase();
  const ls     = node.children ? leafsOf(node) : [];
  const count  = ls.filter(k => sel.has(k)).length;
  const someOn = ls.length > 0 && count > 0 && count < ls.length;
  const indRef = useRef(null);

  useEffect(() => { if (indRef.current) indRef.current.indeterminate = someOn; }, [someOn]);

  if (!nodeMatches(node, lq)) return null;

  if (!node.children) {
    return (
      <label style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: `3px 8px 3px ${depth * 14 + 12}px`,
        cursor: readOnly ? 'default' : 'pointer', fontSize: 12,
        opacity: readOnly && !sel.has(node.key) ? 0.4 : 1,
      }}>
        {!readOnly && (
          <input
            type="checkbox"
            checked={sel.has(node.key)}
            onChange={() => onToggleLeaf(node.key)}
            style={{ accentColor: 'var(--nx-accent)', flexShrink: 0 }}
          />
        )}
        {readOnly && (
          <span style={{ width: 14, flexShrink: 0, color: sel.has(node.key) ? '#0E8C5A' : 'var(--nx-border)' }}>
            <NexusIcon name={sel.has(node.key) ? 'CheckCircle' : 'Circle'} size={12} />
          </span>
        )}
        <span style={{ flex: 1 }}>{node.label}</span>
        <span className="nx-muted" style={{ fontSize: 10, fontFamily: 'monospace', opacity: 0.45 }}>{node.key}</span>
      </label>
    );
  }

  const allOn  = count === ls.length;
  const isOpen = open.has(node.key) || !!q;

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: `4px 6px 4px ${depth * 14 + 6}px`,
        background: depth === 0 ? 'var(--nx-surface-alt)' : 'transparent',
        borderBottom: depth === 0 ? '1px solid var(--nx-border)' : 'none',
      }}>
        {!readOnly && (
          <input
            type="checkbox"
            ref={indRef}
            checked={allOn}
            onChange={() => onToggleGroup(node)}
            style={{ accentColor: 'var(--nx-accent)', flexShrink: 0 }}
          />
        )}
        <span style={{ flex: 1, fontWeight: depth === 0 ? 600 : 500, fontSize: depth === 0 ? 12 : 11, color: 'var(--nx-text)' }}>
          {node.label}
        </span>
        <span style={{
          fontSize: 10, borderRadius: 10, padding: '1px 6px',
          background: count > 0 ? 'var(--nx-accent-tint,rgba(18,90,176,0.08))' : 'transparent',
          color: count > 0 ? 'var(--nx-accent,#125AB0)' : 'var(--nx-text-faint)',
        }}>
          {count}/{ls.length}
        </span>
        <button
          type="button"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--nx-text-muted)' }}
          onClick={() => onToggleOpen(node.key)}
        >
          <NexusIcon name={isOpen ? 'ChevronDown' : 'ChevronRight'} size={12} />
        </button>
      </div>
      {isOpen && (
        <div style={{ borderLeft: '2px solid var(--nx-border)', marginLeft: depth * 14 + 14 }}>
          {node.children.map(child => (
            <TreeNode
              key={child.key}
              node={child}
              depth={depth + 1}
              sel={sel}
              q={q}
              open={open}
              onToggleLeaf={onToggleLeaf}
              onToggleGroup={onToggleGroup}
              onToggleOpen={onToggleOpen}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── FeatureTree ───────────────────────────────────────────────────────────────

function FeatureTree({ selected, onChange, readOnly }) {
  const [q, setQ]       = useState('');
  const [open, setOpen] = useState(new Set());
  const sel = new Set(selected);

  const allGroupKeys = collectGroupKeys(FEATURE_REGISTRY);

  const handleToggleGroup = (node) => {
    const ls    = leafsOf(node);
    const allOn = ls.every(k => sel.has(k));
    const next  = new Set(sel);
    if (allOn) ls.forEach(k => next.delete(k));
    else ls.forEach(k => next.add(k));
    onChange([...next]);
  };

  const handleToggleLeaf = (key) => {
    const next = new Set(sel);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange([...next]);
  };

  const handleToggleOpen = (key) => {
    setOpen(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allLeafs = getAllLeafKeys(FEATURE_REGISTRY);
  const totalSel = allLeafs.filter(k => sel.has(k)).length;

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
        position: 'sticky', top: 0, background: 'var(--nx-surface)', zIndex: 2, paddingBottom: 4,
      }}>
        <div className="nx-search-inline" style={{ flex: 1 }}>
          <NexusIcon name="Search" size={12} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search features…" style={{ fontSize: 12 }} />
          {q && <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }} onClick={() => setQ('')}><NexusIcon name="X" size={11} /></button>}
        </div>
        <span className="nx-muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{totalSel}/{TOTAL_FEATURE_KEYS}</span>
        {!readOnly && (
          <>
            <button type="button" className="nx-btn nx-btn-ghost nx-btn-sm" onClick={() => setOpen(new Set(allGroupKeys))}>Expand All</button>
            <button type="button" className="nx-btn nx-btn-ghost nx-btn-sm" onClick={() => setOpen(new Set())}>Collapse</button>
            <button type="button" className="nx-btn nx-btn-ghost nx-btn-sm" onClick={() => onChange(allLeafs)}>All</button>
            <button type="button" className="nx-btn nx-btn-ghost nx-btn-sm" onClick={() => onChange([])}>None</button>
          </>
        )}
      </div>
      <div style={{ height: readOnly ? 280 : 310, overflowY: 'auto', border: '1px solid var(--nx-border)', borderRadius: 6 }}>
        {FEATURE_REGISTRY.map(node => (
          <TreeNode
            key={node.key}
            node={node}
            depth={0}
            sel={sel}
            q={q}
            open={open}
            onToggleLeaf={handleToggleLeaf}
            onToggleGroup={handleToggleGroup}
            onToggleOpen={handleToggleOpen}
            readOnly={readOnly}
          />
        ))}
      </div>
    </div>
  );
}

// ── PlanForm ──────────────────────────────────────────────────────────────────

function PlanForm({ initial, onSave, onClose, isEdit }) {
  const initRef = useRef(null);
  if (!initRef.current) {
    initRef.current = {
      ...EMPTY_FORM, ...initial,
      featureKeys: normalizeFeatureKeys(Array.isArray(initial.featureKeys) ? initial.featureKeys : []),
      isPublic: initial.isPublic !== undefined ? initial.isPublic : true,
      status:   initial.status   || 'ACTIVE',
      // Coerce all numeric inputs to strings — React <input type="number"> always yields strings,
      // so type divergence here causes isDirty false positives when the user types the same value.
      sortOrder:           String(initial.sortOrder ?? 0),
      priceMonthly:        initial.priceMonthly        != null ? String(initial.priceMonthly)        : '',
      priceAnnual:         initial.priceAnnual         != null ? String(initial.priceAnnual)         : '',
      trialDays:           initial.trialDays           != null ? String(initial.trialDays)           : '14',
      maxWorkspaces:       initial.maxWorkspaces       != null ? String(initial.maxWorkspaces)       : '',
      maxStorageGb:        initial.maxStorageGb        != null ? String(initial.maxStorageGb)        : '',
      maxAgents:           initial.maxAgents           != null ? String(initial.maxAgents)           : '',
      maxBots:             initial.maxBots             != null ? String(initial.maxBots)             : '',
      maxApiCallsPerDay:   initial.maxApiCallsPerDay   != null ? String(initial.maxApiCallsPerDay)   : '',
      maxMessagesPerMonth: initial.maxMessagesPerMonth != null ? String(initial.maxMessagesPerMonth) : '',
    };
  }
  const [form, setForm]     = useState(initRef.current);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [warnClose, setWarnClose] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = (k)   => (e) => set(k, e.target.value);

  const sortArr = a => [...(a || [])].sort();
  const isDirty = JSON.stringify({ ...form, featureKeys: sortArr(form.featureKeys) }) !==
                  JSON.stringify({ ...initRef.current, featureKeys: sortArr(initRef.current.featureKeys) });
  const CODE_RE = /^[a-z0-9_-]+$/;
  const codeOk  = !isEdit ? (form.code.trim() !== '' && CODE_RE.test(form.code.trim())) : true;
  const valid   = codeOk && form.name.trim() && form.tier;

  const handleTierChange = (e) => {
    const tier = e.target.value;
    set('tier', tier);
    if (!isEdit) set('featureKeys', PLAN_FEATURE_DEFAULTS[tier] || []);
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      const fk = resolveDependencies(form.featureKeys || []);

      // Shared updatable fields (allowed by UpdatePlanDto)
      const editableFields = {
        name:                form.name.trim(),
        displayName:         form.displayName.trim() || form.name.trim(),
        description:         form.description.trim(),
        isPublic:            !!form.isPublic,
        sortOrder:           parseInt(form.sortOrder, 10) || 0,
        priceMonthly:        floatOrNull(form.priceMonthly),
        priceAnnual:         floatOrNull(form.priceAnnual),
        currency:            form.currency || 'INR',
        trialDays:           intOrNull(form.trialDays) ?? 14,
        maxWorkspaces:       intOrNull(form.maxWorkspaces),
        maxStorageGb:        intOrNull(form.maxStorageGb),
        maxAgents:           intOrNull(form.maxAgents),
        maxBots:             intOrNull(form.maxBots),
        maxApiCallsPerDay:   intOrNull(form.maxApiCallsPerDay),
        maxMessagesPerMonth: intOrNull(form.maxMessagesPerMonth),
        featureKeys:         fk,
        brandingAllowed:     fk.some(k => k.startsWith('org.branding.')),
        customDomainAllowed: fk.includes('org.whitelabel'),
        aiEnabled:           fk.some(k => k.startsWith('ai.')),
        analyticsEnabled:    fk.some(k => k.startsWith('analytics.')),
      };

      let result;
      if (isEdit) {
        // PATCH: code and tier are immutable — must NOT be sent
        const patchBody = { ...editableFields };
        if (form.status) patchBody.status = form.status;
        result = await planApi.update(initial.id, patchBody);
      } else {
        // POST: code and tier are required at creation time
        result = await planApi.create({
          ...editableFields,
          code: form.code.trim().toLowerCase(),
          tier: form.tier,
        });
      }
      onSave(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (isDirty) { setWarnClose(true); return; }
    onClose();
  };

  const sectionLabel = (text) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{text}</div>
  );

  return (
    <TenancyModal
      title={isEdit ? 'Edit Plan' : 'Create Plan'}
      subtitle={isEdit ? (form.name || '') : null}
      icon="Package"
      onClose={handleClose}
      width={1200}
      dialog
      footer={
        <>
          {warnClose ? (
            <>
              <span style={{ fontSize: 12, color: 'var(--nx-danger)', flex: 1 }}>Unsaved changes — discard?</span>
              <Btn variant="ghost" onClick={() => setWarnClose(false)}>Keep Editing</Btn>
              <Btn variant="danger" onClick={onClose}>Discard</Btn>
            </>
          ) : (
            <>
              <Btn variant="ghost" onClick={handleClose} disabled={saving}>Cancel</Btn>
              <Btn variant="primary" icon={saving ? 'Loader' : 'Check'} disabled={!valid || saving} onClick={handleSave}>
                {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Plan'}
              </Btn>
            </>
          )}
        </>
      }
    >
      {error && (
        <div style={{ background: 'var(--nx-danger-tint,#fef2f2)', color: 'var(--nx-danger,#DC2626)', borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Identity */}
      {sectionLabel('Identity')}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 18 }}>
        <div className="nx-form-row">
          <label>Plan Name <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
          <input className="nx-input" value={form.name} onChange={inp('name')} placeholder="e.g. Professional" autoFocus={!isEdit} />
        </div>
        <div className="nx-form-row">
          <label>Display Name</label>
          <input className="nx-input" value={form.displayName} onChange={inp('displayName')} placeholder="Shown to customers (defaults to Plan Name)" />
        </div>
        {!isEdit && (
          <div className="nx-form-row">
            <label>Code <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
            <input className="nx-input" value={form.code}
              onChange={e => set('code', e.target.value.toLowerCase())}
              placeholder="e.g. professional" style={{ fontFamily: 'monospace' }} />
            {form.code && !CODE_RE.test(form.code) ? (
              <div style={{ color: 'var(--nx-danger)', fontSize: 11, marginTop: 3 }}>Only a–z, 0–9, hyphens, underscores</div>
            ) : (
              <div className="nx-muted nx-mini" style={{ marginTop: 3 }}>Lowercase, immutable after creation</div>
            )}
          </div>
        )}
        <div className="nx-form-row" style={isEdit ? {} : {}}>
          <label>Tier <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
          <select className="nx-input" value={form.tier} onChange={handleTierChange} disabled={isEdit}>
            {TIER_ORDER.map(t => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
          </select>
        </div>
        {isEdit && (
          <div className="nx-form-row">
            <label>Status</label>
            <select className="nx-input" value={form.status} onChange={inp('status')}>
              <option value="DRAFT">Draft</option>
              <option value="ACTIVE">Active</option>
            </select>
            <div className="nx-muted nx-mini" style={{ marginTop: 3 }}>Use Archive action to archive a plan</div>
          </div>
        )}
        <div className="nx-form-row" style={{ gridColumn: '1/-1' }}>
          <label>Description</label>
          <textarea className="nx-input" rows={2} value={form.description} onChange={inp('description')} placeholder="Brief description" style={{ resize: 'vertical' }} />
        </div>
        <div className="nx-form-row" style={{ display: 'flex', alignItems: 'center', gap: 10, flexDirection: 'row' }}>
          <label style={{ margin: 0 }}>Public Plan</label>
          <input type="checkbox" checked={!!form.isPublic} onChange={e => set('isPublic', e.target.checked)} style={{ accentColor: 'var(--nx-accent)', width: 16, height: 16 }} />
          <span className="nx-muted" style={{ fontSize: 12 }}>Visible in customer-facing plan pickers</span>
        </div>
      </div>

      {/* Pricing */}
      {sectionLabel('Pricing')}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 18 }}>
        <div className="nx-form-row">
          <label>Monthly Price</label>
          <input className="nx-input" type="number" min="0" step="0.01" value={form.priceMonthly} onChange={inp('priceMonthly')} placeholder="Leave blank for custom" />
        </div>
        <div className="nx-form-row">
          <label>Annual Price</label>
          <input className="nx-input" type="number" min="0" step="0.01" value={form.priceAnnual} onChange={inp('priceAnnual')} placeholder="Leave blank for custom" />
        </div>
        <div className="nx-form-row">
          <label>Currency</label>
          <select className="nx-input" value={form.currency} onChange={inp('currency')}>
            {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="nx-form-row">
          <label>Trial Days</label>
          <input className="nx-input" type="number" min="0" value={form.trialDays} onChange={inp('trialDays')} placeholder="14" />
        </div>
      </div>

      {/* Limits */}
      {sectionLabel('Limits')}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 18 }}>
        <div className="nx-form-row">
          <label>Max Workspaces</label>
          <input className="nx-input" type="number" min="1" value={form.maxWorkspaces} onChange={inp('maxWorkspaces')} placeholder="∞ unlimited" />
        </div>
        <div className="nx-form-row">
          <label>Max Storage (GB)</label>
          <input className="nx-input" type="number" min="1" value={form.maxStorageGb} onChange={inp('maxStorageGb')} placeholder="∞ unlimited" />
        </div>
        <div className="nx-form-row">
          <label>Max AI Agents</label>
          <input className="nx-input" type="number" min="0" value={form.maxAgents} onChange={inp('maxAgents')} placeholder="∞ unlimited" />
        </div>
        <div className="nx-form-row">
          <label>Max Bots</label>
          <input className="nx-input" type="number" min="0" value={form.maxBots} onChange={inp('maxBots')} placeholder="∞ unlimited" />
        </div>
        <div className="nx-form-row">
          <label>Max API Calls/Day</label>
          <input className="nx-input" type="number" min="0" value={form.maxApiCallsPerDay} onChange={inp('maxApiCallsPerDay')} placeholder="∞ unlimited" />
        </div>
        <div className="nx-form-row">
          <label>Max Messages/Month</label>
          <input className="nx-input" type="number" min="0" value={form.maxMessagesPerMonth} onChange={inp('maxMessagesPerMonth')} placeholder="∞ unlimited" />
        </div>
        <div className="nx-form-row">
          <label>Sort Order</label>
          <input className="nx-input" type="number" min="0" value={form.sortOrder} onChange={inp('sortOrder')} />
        </div>
      </div>

      {/* Entitlements */}
      <div style={{ borderTop: '1px solid var(--nx-border)', paddingTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>
            Plan Entitlements
          </div>
          <button type="button" className="nx-btn nx-btn-ghost nx-btn-sm"
            onClick={() => set('featureKeys', PLAN_FEATURE_DEFAULTS[form.tier] || [])}>
            {isEdit ? 'Reset to tier defaults' : 'Seed from tier defaults'}
          </button>
        </div>
        <FeatureTree selected={form.featureKeys} onChange={keys => set('featureKeys', keys)} />
      </div>
    </TenancyModal>
  );
}

// ── CloneModal ────────────────────────────────────────────────────────────────

function CloneModal({ plan, onCloned, onClose }) {
  const [form, setForm] = useState({
    code: plan.code + '_copy',
    name: plan.name + ' (Copy)',
    description: plan.description || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleClone = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await planApi.clone(plan.id, {
        code: form.code.trim().toLowerCase(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
      });
      onCloned(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const cloneCodeOk = form.code.trim() !== '' && /^[a-z0-9_-]+$/.test(form.code.trim());

  return (
    <TenancyModal title={`Clone "${plan.name}"`} icon="Copy" onClose={onClose} width={480} dialog
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn variant="primary" icon={saving ? 'Loader' : 'Copy'} disabled={!cloneCodeOk || !form.name.trim() || saving} onClick={handleClone}>
            {saving ? 'Cloning…' : 'Clone Plan'}
          </Btn>
        </>
      }
    >
      {error && (
        <div style={{ background: 'var(--nx-danger-tint,#fef2f2)', color: 'var(--nx-danger)', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}
      <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 16 }}>
        Creates an exact copy with all feature keys, limits, and pricing. Status is set to <strong>DRAFT</strong> — publish when ready.
      </p>
      <div className="nx-form-row">
        <label>New Plan Name <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
        <input className="nx-input" autoFocus value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
      </div>
      <div className="nx-form-row">
        <label>New Code <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
        <input className="nx-input" value={form.code} style={{ fontFamily: 'monospace' }}
          onChange={e => setForm(f => ({ ...f, code: e.target.value.toLowerCase() }))} />
        {form.code && !cloneCodeOk ? (
          <div style={{ color: 'var(--nx-danger)', fontSize: 11, marginTop: 3 }}>Only a–z, 0–9, hyphens, underscores</div>
        ) : (
          <div className="nx-muted nx-mini" style={{ marginTop: 3 }}>Lowercase, unique</div>
        )}
      </div>
      <div className="nx-form-row">
        <label>Description</label>
        <textarea className="nx-input" rows={2} value={form.description}
          style={{ resize: 'vertical' }}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Leave blank to copy source description" />
      </div>
    </TenancyModal>
  );
}

// ── ViewPlanModal ─────────────────────────────────────────────────────────────

function ViewPlanModal({ plan, onClose }) {
  const fk = normalizeFeatureKeys(Array.isArray(plan.featureKeys) ? plan.featureKeys : []);

  const domainSummary = FEATURE_REGISTRY.map(domain => {
    const domainLeafs = getAllLeafKeys([domain]);
    const enabled = domainLeafs.filter(k => fk.includes(k));
    return { label: domain.label, key: domain.key, enabled: enabled.length, total: domainLeafs.length };
  });

  const row = (label, value) => (
    <div style={{ display: 'flex', fontSize: 13, padding: '5px 0', borderBottom: '1px solid var(--nx-border)' }}>
      <span style={{ width: 150, flexShrink: 0, color: 'var(--nx-text-muted)', fontSize: 12 }}>{label}</span>
      <span style={{ flex: 1, fontFamily: typeof value === 'string' && value.match(/^[a-z0-9_-]+$/) ? 'monospace' : undefined }}>{value ?? <span style={{ color: 'var(--nx-text-faint)' }}>—</span>}</span>
    </div>
  );

  return (
    <TenancyModal title={`Plan — ${plan.name}`} subtitle={plan.code} icon="Eye" onClose={onClose} width={860} dialog
      footer={<Btn variant="ghost" onClick={onClose}>Close</Btn>}
    >
      {/* Identity */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Identity</div>
      <div style={{ marginBottom: 16, border: '1px solid var(--nx-border)', borderRadius: 6, padding: '4px 12px' }}>
        {row('Plan ID', <span style={{ fontSize: 11, fontFamily: 'monospace', opacity: 0.7 }}>{plan.id}</span>)}
        {row('Code', plan.code)}
        {row('Name', plan.name)}
        {row('Display Name', plan.displayName || plan.name)}
        {row('Tier', <Pill tone={TIER_TONE[plan.tier]}>{TIER_LABEL[plan.tier]}</Pill>)}
        {row('Status', <Pill tone={STATUS_TONE[plan.status]}>{plan.status}</Pill>)}
        {row('Version', `v${plan.version}`)}
        {row('Default', plan.isDefault ? <Pill tone="purple">Yes</Pill> : 'No')}
        {row('Public', plan.isPublic ? 'Yes' : 'No')}
        {row('Trial Days', plan.trialDays != null ? `${plan.trialDays} days` : '—')}
        {row('Sort Order', plan.sortOrder)}
      </div>

      {/* Description */}
      {plan.description && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Description</div>
          <p style={{ fontSize: 13, color: 'var(--nx-text)', marginBottom: 16, lineHeight: 1.6 }}>{plan.description}</p>
        </>
      )}

      {/* Pricing */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Pricing</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
        {[
          ['Monthly', fmtPrice(plan.priceMonthly, plan.currency || 'INR')],
          ['Annual', fmtPrice(plan.priceAnnual, plan.currency || 'INR')],
          ['Currency', plan.currency || 'INR'],
          ['Subscribers', plan._count?.subscriptions ?? 0],
        ].map(([label, value]) => (
          <div key={label} style={{ border: '1px solid var(--nx-border)', borderRadius: 6, padding: '8px 12px' }}>
            <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginBottom: 4 }}>{label}</div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Limits */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Limits</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
        {[
          ['Workspaces', plan.maxWorkspaces ?? '∞'],
          ['Storage',    plan.maxStorageGb  != null ? plan.maxStorageGb + ' GB' : '∞'],
          ['AI Agents',  plan.maxAgents     ?? '∞'],
          ['Bots',       plan.maxBots       ?? '∞'],
          ['API calls/d',plan.maxApiCallsPerDay ?? '∞'],
          ['Msgs/month', plan.maxMessagesPerMonth ?? '∞'],
        ].map(([label, value]) => (
          <div key={label} style={{ border: '1px solid var(--nx-border)', borderRadius: 6, padding: '8px 10px' }}>
            <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginBottom: 2 }}>{label}</div>
            <div style={{ fontWeight: 700, fontSize: 14, fontFamily: 'monospace' }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Feature Coverage */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        Feature Coverage — {fk.length}/{TOTAL_FEATURE_KEYS} keys
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
        {domainSummary.map(d => {
          const pct = d.total > 0 ? Math.round((d.enabled / d.total) * 100) : 0;
          return (
            <div key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 120, minWidth: 100, fontSize: 12, color: 'var(--nx-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
              <div style={{ flex: 1, height: 6, background: 'var(--nx-border)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: pct + '%', background: pct === 100 ? '#0E8C5A' : pct > 0 ? 'var(--nx-accent)' : 'transparent', borderRadius: 3 }} />
              </div>
              <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', width: 60, textAlign: 'right' }}>
                {d.enabled}/{d.total}
              </span>
              <Pill tone={pct === 100 ? 'green' : pct > 0 ? 'blue' : 'gray'} style={{ fontSize: 10 }}>{pct}%</Pill>
            </div>
          );
        })}
      </div>

      {/* Feature Tree — read-only */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Entitlements Detail</div>
      <FeatureTree selected={fk} onChange={() => {}} readOnly />

      {/* Metadata */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 16, marginBottom: 8 }}>Metadata</div>
      <div style={{ border: '1px solid var(--nx-border)', borderRadius: 6, padding: '4px 12px' }}>
        {row('Created', fmtDate(plan.createdAt))}
        {row('Last Updated', fmtDate(plan.updatedAt))}
        {row('Archived At', fmtDate(plan.archivedAt))}
      </div>
    </TenancyModal>
  );
}

// ── PlanCompareModal ──────────────────────────────────────────────────────────

// Defined at module level — NOT inside PlanCompareModal to avoid React unmount/remount on every render.
function PlanCol({ plan }) {
  if (!plan) return (
    <div style={{ flex: 1, border: '1px dashed var(--nx-border)', borderRadius: 8, padding: 16, textAlign: 'center', color: 'var(--nx-text-faint)', fontSize: 13 }}>
      Select a plan
    </div>
  );
  const fk = normalizeFeatureKeys(Array.isArray(plan.featureKeys) ? plan.featureKeys : []);
  const domainRows = FEATURE_REGISTRY.map(domain => {
    const leafs = getAllLeafKeys([domain]);
    return { label: domain.label, enabled: leafs.filter(k => fk.includes(k)).length, total: leafs.length };
  });
  return (
    <div style={{ flex: 1, border: '1px solid var(--nx-border)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', background: 'var(--nx-surface-alt)', borderBottom: '1px solid var(--nx-border)' }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{plan.name}</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <Pill tone={TIER_TONE[plan.tier]}>{TIER_LABEL[plan.tier]}</Pill>
          <Pill tone={STATUS_TONE[plan.status]}>{plan.status}</Pill>
        </div>
      </div>
      <div style={{ padding: '10px 14px' }}>
        <div style={{ fontSize: 12, marginBottom: 8 }}>
          <div><span className="nx-muted">Monthly:</span> {fmtPrice(plan.priceMonthly, plan.currency || 'INR')}</div>
          <div><span className="nx-muted">Annual:</span>  {fmtPrice(plan.priceAnnual,  plan.currency || 'INR')}</div>
          <div><span className="nx-muted">Features:</span> {fk.length}/{TOTAL_FEATURE_KEYS}</div>
          <div><span className="nx-muted">Storage:</span>  {plan.maxStorageGb != null ? plan.maxStorageGb + ' GB' : '∞'}</div>
          <div><span className="nx-muted">Workspaces:</span> {plan.maxWorkspaces ?? '∞'}</div>
        </div>
        <div style={{ borderTop: '1px solid var(--nx-border)', paddingTop: 8 }}>
          {domainRows.map(d => (
            <div key={d.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
              <span style={{ color: 'var(--nx-text-muted)' }}>{d.label}</span>
              <span style={{ fontFamily: 'monospace', color: d.enabled > 0 ? 'var(--nx-accent)' : 'var(--nx-text-faint)' }}>
                {d.enabled}/{d.total}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PlanCompareModal({ initialPlanId, onClose }) {
  const [allPlans,     setAllPlans]     = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [fetchError,   setFetchError]   = useState('');
  const [leftId,       setLeftId]       = useState(initialPlanId || '');
  const [rightId,      setRightId]      = useState('');

  useEffect(() => {
    planApi.list({ pageSize: 100 })
      .then(res => {
        const data = Array.isArray(res) ? res : (res?.data ?? []);
        setAllPlans(data);
        if (!initialPlanId && data.length > 0) setLeftId(data[0].id);
      })
      .catch(e => setFetchError(e.message || 'Failed to load plans'))
      .finally(() => setLoadingPlans(false));
  }, []);

  const left  = allPlans.find(p => p.id === leftId);
  const right = allPlans.find(p => p.id === rightId);

  return (
    <TenancyModal title="Compare Plans" icon="Columns" onClose={onClose} width={860} dialog
      footer={<Btn variant="ghost" onClick={onClose}>Close</Btn>}
    >
      {fetchError && (
        <div style={{ color: 'var(--nx-danger)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <NexusIcon name="AlertCircle" size={14} />{fetchError}
        </div>
      )}
      {loadingPlans ? (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <div className="nx-cc-skeleton" style={{ flex: 1, height: 36, borderRadius: 6 }} />
          <div className="nx-cc-skeleton" style={{ flex: 1, height: 36, borderRadius: 6 }} />
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 4 }}>Plan A</label>
            <select className="nx-input" value={leftId} onChange={e => setLeftId(e.target.value)}>
              <option value="">— Select —</option>
              {allPlans.map(p => <option key={p.id} value={p.id}>{p.name} ({TIER_LABEL[p.tier]}) — {p.status}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: 'var(--nx-text-muted)', display: 'block', marginBottom: 4 }}>Plan B</label>
            <select className="nx-input" value={rightId} onChange={e => setRightId(e.target.value)}>
              <option value="">— Select —</option>
              {allPlans.filter(p => p.id !== leftId).map(p => <option key={p.id} value={p.id}>{p.name} ({TIER_LABEL[p.tier]}) — {p.status}</option>)}
            </select>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto' }}>
        <div style={{ flex: 1, minWidth: 220 }}><PlanCol plan={left} /></div>
        <div style={{ flex: 1, minWidth: 220 }}><PlanCol plan={right} /></div>
      </div>
    </TenancyModal>
  );
}

// ── ActionsMenu ───────────────────────────────────────────────────────────────

function ActionsMenu({ plan, onView, onEdit, onClone, onSetDefault, onArchive, onRestore, onHistory, onCompare }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top: 0, bottom: undefined, right: 0 });
  const triggerRef      = useRef(null);
  const menuRef         = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Check BOTH the trigger element AND the portal menu div — createPortal renders the menu
    // into document.body (not inside triggerRef.current in the DOM), so contains() returns false
    // for portal children. Without the menuRef check, any mousedown inside the portal fires
    // setOpen(false) and React flushes it before the click event fires, killing the action.
    const onMouse = (e) => {
      const inTrigger = triggerRef.current?.contains(e.target);
      const inMenu    = menuRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown',   onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown',   onKey);
    };
  }, [open]);

  const handleOpen = () => {
    if (!open && triggerRef.current) {
      const rect       = triggerRef.current.getBoundingClientRect();
      const MENU_H     = 260;
      const right      = window.innerWidth - rect.right;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < MENU_H && rect.top > MENU_H) {
        setPos({ top: undefined, bottom: window.innerHeight - rect.top + 4, right });
      } else {
        setPos({ top: rect.bottom + 4, bottom: undefined, right });
      }
    }
    setOpen(v => !v);
  };

  const item = (icon, label, action, danger) => (
    <button
      type="button"
      key={label}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        padding: '6px 12px', background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 13, textAlign: 'left', color: danger ? 'var(--nx-danger,#DC2626)' : 'var(--nx-text)',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--nx-surface-alt)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
      onClick={() => { action(); setOpen(false); }}
    >
      <NexusIcon name={icon} size={13} style={{ flexShrink: 0 }} />
      {label}
    </button>
  );

  return (
    <div ref={triggerRef} style={{ display: 'inline-block' }}>
      <Btn variant="ghost" size="sm" icon="MoreVertical" onClick={handleOpen} aria-label="Actions" />
      {open && createPortal(
        <div ref={menuRef} style={{
          position: 'fixed', top: pos.top, bottom: pos.bottom, right: pos.right, zIndex: 500,
          background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
          borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          minWidth: 170, overflow: 'hidden', padding: '4px 0',
        }}>
          {item('Eye',      'View Details',  onView)}
          {item('Columns',  'Compare',       onCompare)}
          {plan.status !== 'ARCHIVED' && item('Edit2', 'Edit', onEdit)}
          {item('Copy',     'Clone',         onClone)}
          {plan.status === 'ACTIVE' && !plan.isDefault && item('Star', 'Set as Default', onSetDefault)}
          {item('Clock',    'Version History', onHistory)}
          <div style={{ borderTop: '1px solid var(--nx-border)', margin: '4px 0' }} />
          {plan.status === 'ACTIVE'   && item('Archive',   'Archive', onArchive, true)}
          {plan.status === 'ARCHIVED' && item('RefreshCw', 'Restore', onRestore)}
          {plan.status === 'DRAFT'    && item('Archive',   'Archive', onArchive, true)}
        </div>,
        document.body
      )}
    </div>
  );
}

// ── ArchiveConfirmModal ───────────────────────────────────────────────────────

function ArchiveConfirmModal({ plan, onConfirm, onClose, archiving }) {
  const subCount = plan._count?.subscriptions ?? 0;
  const blocked  = subCount > 0 || plan.isDefault;

  return (
    <TenancyModal title="Archive Plan" icon="Archive" onClose={onClose} width={440} danger={!blocked} dialog
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} disabled={archiving}>Cancel</Btn>
          {!blocked && (
            <Btn variant="danger" icon={archiving ? 'Loader' : 'Archive'} onClick={onConfirm} disabled={archiving}>
              {archiving ? 'Archiving…' : 'Archive Plan'}
            </Btn>
          )}
        </>
      }
    >
      <p style={{ fontSize: 13, marginBottom: 12 }}>
        Archive <strong>{plan.name}</strong> ({plan.code})?
      </p>
      {plan.isDefault && (
        <div style={{ background: '#FEF2F2', color: '#DC2626', borderRadius: 6, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
          <strong>Cannot archive</strong> — this is the default plan. Assign a different default plan first.
        </div>
      )}
      {!plan.isDefault && subCount > 0 && (
        <div style={{ background: '#FEF2F2', color: '#DC2626', borderRadius: 6, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
          <strong>Cannot archive</strong> — {subCount} organization{subCount !== 1 ? 's are' : ' is'} currently subscribed to this plan. Move them to a different plan first.
        </div>
      )}
      {!blocked && (
        <div style={{ background: '#FFFBEB', border: '1px solid #F59E0B', borderRadius: 6, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
          Archived plans cannot be assigned to new organizations. Existing subscriptions are not affected.
        </div>
      )}
    </TenancyModal>
  );
}

// ── SetDefaultConfirmModal ────────────────────────────────────────────────────

function SetDefaultConfirmModal({ plan, currentDefault, onConfirm, onClose, saving }) {
  return (
    <TenancyModal title="Set Default Plan" icon="Star" onClose={onClose} width={440} dialog
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" icon={saving ? 'Loader' : 'Star'} disabled={saving} onClick={onConfirm}>
            {saving ? 'Saving…' : 'Set as Default'}
          </Btn>
        </>
      }
    >
      <p style={{ fontSize: 13, marginBottom: 12 }}>
        Set <strong>{plan.name}</strong> as the default plan for new organizations?
      </p>
      {currentDefault && currentDefault.id !== plan.id && (
        <div style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 6, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--nx-text-muted)' }}>Current default:</span>
            <strong>{currentDefault.name}</strong>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <span style={{ color: 'var(--nx-text-muted)' }}>New default:</span>
            <strong>{plan.name}</strong>
          </div>
        </div>
      )}
    </TenancyModal>
  );
}

// ── RestoreConfirmModal ───────────────────────────────────────────────────────

function RestoreConfirmModal({ plan, onConfirm, onClose, restoring }) {
  return (
    <TenancyModal title="Restore Plan" icon="RefreshCw" onClose={onClose} width={420} dialog
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} disabled={restoring}>Cancel</Btn>
          <Btn variant="primary" icon={restoring ? 'Loader' : 'RefreshCw'} onClick={onConfirm} disabled={restoring}>
            {restoring ? 'Restoring…' : 'Restore Plan'}
          </Btn>
        </>
      }
    >
      <p style={{ fontSize: 13, marginBottom: 12 }}>
        Restore <strong>{plan.name}</strong> ({plan.code}) to active status?
      </p>
      <div style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 6, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>
        The plan will become available for assignment to new organizations.
      </div>
    </TenancyModal>
  );
}

// ── VersionHistoryDrawer ──────────────────────────────────────────────────────

function VersionHistoryDrawer({ plan, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError('');
    planApi.getHistory(plan.id)
      .then(data => setHistory(Array.isArray(data) ? data : []))
      .catch(e => setError(e.message || 'Failed to load history'))
      .finally(() => setLoading(false));
  }, [plan.id]);

  const ACTION_ICONS = {
    'created plan': 'Plus',
    'updated plan': 'Edit2',
    'archived plan': 'Archive',
    'restored plan': 'RefreshCw',
    'cloned plan': 'Copy',
    'set default plan': 'Star',
  };

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 199 }}
        onClick={onClose}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '100vw',
        background: 'var(--nx-surface)', borderLeft: '1px solid var(--nx-border)',
        zIndex: 200, display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.10)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', borderBottom: '1px solid var(--nx-border)', flexShrink: 0 }}>
          <NexusIcon name="Clock" size={16} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Version History</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{plan.name} — v{plan.version}</div>
          </div>
          <Btn variant="ghost" size="sm" icon="X" onClick={onClose} />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[1, 2, 3].map(i => (
                <div key={i} className="nx-cc-skeleton" style={{ height: 64, borderRadius: 6 }} />
              ))}
            </div>
          )}

          {!loading && error && (
            <div style={{ color: 'var(--nx-danger)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
              <NexusIcon name="AlertCircle" size={14} />
              {error}
            </div>
          )}

          {!loading && !error && history.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--nx-text-muted)' }}>
              <div style={{ opacity: 0.3, marginBottom: 12 }}><NexusIcon name="Clock" size={28} /></div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>No version history available.</div>
              <div style={{ fontSize: 12 }}>History appears here when the plan is created or modified.</div>
            </div>
          )}

          {!loading && !error && history.length > 0 && (
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 13, top: 0, bottom: 0, width: 1, background: 'var(--nx-border)' }} />
              {history.map((entry, i) => (
                <div key={entry.id || i} style={{ display: 'flex', gap: 12, marginBottom: 16, position: 'relative' }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative', zIndex: 1,
                  }}>
                    <NexusIcon name={ACTION_ICONS[entry.action] || 'Activity'} size={12} />
                  </div>
                  <div style={{ flex: 1, paddingTop: 2 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{entry.action}</div>
                    {entry.target && (
                      <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{entry.target}</div>
                    )}
                    <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 11, color: 'var(--nx-text-faint)' }}>
                      {entry.who && <span>{entry.who}</span>}
                      <span>{new Date(entry.createdAt).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function Plans() {
  const toast = useToast();

  // Data
  const [plans,      setPlans]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  // Filters + sort + pagination
  const [q,          setQ]          = useState('');
  const [tierFilt,   setTierFilt]   = useState('');
  const [statFilt,   setStatFilt]   = useState('ACTIVE');
  const [sortKey,    setSortKey]    = useState('name');
  const [sortDir,    setSortDir]    = useState('asc');
  const [page,       setPage]       = useState(1);

  // Modals — create / edit / clone / view / compare
  const [creating,    setCreating]    = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [cloning,     setCloning]     = useState(null);
  const [viewing,     setViewing]     = useState(null);
  const [comparing,   setComparing]   = useState(false);
  const [compareInit, setCompareInit] = useState(null);

  // Confirmation modals
  const [archiving,       setArchiving]       = useState(null); // plan to archive
  const [archivingBusy,   setArchivingBusy]   = useState(false);
  const [settingDefault,  setSettingDefault]  = useState(null); // plan to set default
  const [settingDefBusy,  setSettingDefBusy]  = useState(false);
  const [restoring,       setRestoring]       = useState(null); // plan to restore
  const [restoringBusy,   setRestoringBusy]   = useState(false);

  // Side drawer
  const [viewingHistory, setViewingHistory] = useState(null);

  // Accurate stat counts independent of the current filter/page
  const [counts,  setCounts]  = useState({ active: 0, draft: 0, archived: 0, total: 0 });
  const [defPlan, setDefPlan] = useState(null);

  const loadMeta = useCallback(async () => {
    try {
      const [aRes, dRes, rRes] = await Promise.all([
        planApi.list({ status: 'ACTIVE',   pageSize: 50 }),
        planApi.list({ status: 'DRAFT',    pageSize: 1  }),
        planApi.list({ status: 'ARCHIVED', pageSize: 1  }),
      ]);
      const pg = r => r?.pagination?.total ?? (Array.isArray(r) ? r.length : 0);
      const aCount = pg(aRes), dCount = pg(dRes), rCount = pg(rRes);
      setCounts({ active: aCount, draft: dCount, archived: rCount, total: aCount + dCount + rCount });
      const aData = Array.isArray(aRes) ? aRes : (aRes?.data ?? []);
      setDefPlan(aData.find(p => p.isDefault) ?? null);
    } catch { /* non-fatal — stat cards fall back to 0 */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await planApi.list({
        status:   statFilt  || undefined,
        search:   q         || undefined,
        tier:     tierFilt  || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      const data = Array.isArray(res) ? res : (res?.data ?? []);
      setPlans(data);
      setTotal(res?.pagination?.total ?? data.length);
      setTotalPages(res?.pagination?.totalPages ?? 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statFilt, q, tierFilt, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  // Client-side sort only (filtering is server-side)
  const sorted = [...plans].sort((a, b) => {
    let av, bv;
    if (sortKey === 'name')    { av = a.name;   bv = b.name; }
    else if (sortKey === 'tier')   { av = TIER_ORDER.indexOf(a.tier);  bv = TIER_ORDER.indexOf(b.tier); }
    else if (sortKey === 'price')  { av = a.priceMonthly ?? -1; bv = b.priceMonthly ?? -1; }
    else if (sortKey === 'subs')   { av = a._count?.subscriptions ?? 0; bv = b._count?.subscriptions ?? 0; }
    else if (sortKey === 'version') { av = a.version; bv = b.version; }
    else { av = a[sortKey]; bv = b[sortKey]; }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1  : -1;
    return 0;
  });

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortTh = ({ children, icon, sortable, sk, end }) => {
    const active = sortKey === sk;
    if (!sortable) return <Th icon={icon} end={end}>{children}</Th>;
    return (
      <th
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        onClick={() => handleSort(sk)}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {icon && <NexusIcon name={icon} size={11} />}
          {children}
          {active ? (
            <NexusIcon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={11} />
          ) : (
            <NexusIcon name="ChevronsUpDown" size={10} style={{ opacity: 0.3 }} />
          )}
        </span>
      </th>
    );
  };

  // Stat counts sourced from loadMeta() — accurate across all pages and filters
  const { active: activeCount, draft: draftCount, archived: archivedCount } = counts;

  // ── CRUD callbacks ──────────────────────────────────────────────────────────

  const onCreated = (plan) => {
    setCreating(false);
    toast({ tone: 'success', icon: 'Check', title: `Plan "${plan.name}" created` });
    load();
    loadMeta();
  };

  const onUpdated = (plan) => {
    setEditing(null);
    toast({ tone: 'success', icon: 'Check', title: `Plan "${plan.name}" updated` });
    load();
    loadMeta();
  };

  const onCloned = (plan) => {
    setCloning(null);
    toast({ tone: 'success', icon: 'Copy', title: `Plan "${plan.name}" cloned as DRAFT` });
    load();
    loadMeta();
  };

  const doArchiveConfirm = async () => {
    if (!archiving) return;
    setArchivingBusy(true);
    try {
      await planApi.archive(archiving.id);
      toast({ tone: 'warning', icon: 'Archive', title: `Plan "${archiving.name}" archived` });
      setArchiving(null);
      load();
      loadMeta();
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Archive failed', desc: e.message });
    } finally {
      setArchivingBusy(false);
    }
  };

  const doSetDefaultConfirm = async () => {
    if (!settingDefault) return;
    setSettingDefBusy(true);
    try {
      await planApi.setDefault(settingDefault.id);
      toast({ tone: 'success', icon: 'Star', title: `"${settingDefault.name}" is now the default plan` });
      setSettingDefault(null);
      load();
      loadMeta();
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Set default failed', desc: e.message });
    } finally {
      setSettingDefBusy(false);
    }
  };

  const doRestoreConfirm = async () => {
    if (!restoring) return;
    setRestoringBusy(true);
    try {
      await planApi.restore(restoring.id);
      toast({ tone: 'success', icon: 'RefreshCw', title: `Plan "${restoring.name}" restored` });
      setRestoring(null);
      load();
      loadMeta();
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Restore failed', desc: e.message });
    } finally {
      setRestoringBusy(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Plans"
        sub={(() => { const t = counts.total || total; return `${t} plan${t !== 1 ? 's' : ''} · ${counts.active} active`; })()}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" icon="Columns" onClick={() => { setCompareInit(null); setComparing(true); }}>Compare</Btn>
            <Btn variant="ghost" icon="RefreshCw" onClick={load} disabled={loading} aria-label="Refresh" />
            <Btn variant="primary" icon="Plus" onClick={() => setCreating(true)}>Create Plan</Btn>
          </div>
        }
      />

      <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <StatCard label="Total Plans"  value={counts.total}                        icon="Package" color="#125AB0" />
        <StatCard label="Active"       value={activeCount}                         icon="Check"   color="#0E8C5A" />
        <StatCard label="Draft / Arch" value={`${draftCount} / ${archivedCount}`}  icon="Archive" color="#64748B" />
        <StatCard label="Default Plan" value={defPlan?.name ?? '—'}                icon="Star"    color="#6E3FBA" />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, margin: '14px 0', flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="nx-search-inline" style={{ width: 260 }}>
          <NexusIcon name="Search" size={13} />
          <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="Search plans…" />
          {q && <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }} onClick={() => { setQ(''); setPage(1); }}><NexusIcon name="X" size={11} /></button>}
        </div>
        <select className="nx-input" style={{ width: 160, fontSize: 13 }} value={tierFilt} onChange={e => { setTierFilt(e.target.value); setPage(1); }}>
          <option value="">All Tiers</option>
          {TIER_ORDER.map(t => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
        </select>
        <select className="nx-input" style={{ width: 150, fontSize: 13 }} value={statFilt} onChange={e => { setStatFilt(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="DRAFT">Draft</option>
          <option value="ARCHIVED">Archived</option>
        </select>
        {(q || tierFilt || statFilt !== 'ACTIVE') && (
          <Btn variant="ghost" size="sm" onClick={() => { setQ(''); setTierFilt(''); setStatFilt('ACTIVE'); setPage(1); }}>Clear</Btn>
        )}
        <span className="nx-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>
          {total} plan{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: 'var(--nx-danger-tint,#fef2f2)', color: 'var(--nx-danger)', borderRadius: 8, padding: '12px 16px', marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="AlertCircle" size={14} />{error}
          <Btn variant="ghost" size="sm" onClick={load} style={{ marginLeft: 'auto' }}>Retry</Btn>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="nx-cc-skeleton" style={{ height: 52, borderRadius: 8 }} />)}
        </div>
      )}

      {/* Empty */}
      {!loading && !error && sorted.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--nx-text-muted)' }}>
          <div style={{ opacity: 0.3, marginBottom: 12 }}><NexusIcon name="Package" size={32} /></div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No plans found</div>
          <div style={{ fontSize: 13 }}>
            {(q || tierFilt || statFilt !== 'ACTIVE') ? 'Try adjusting your filters.' : 'Create your first plan to get started.'}
          </div>
        </div>
      )}

      {/* Table */}
      {!loading && sorted.length > 0 && (
        <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
          <thead><tr>
            <SortTh icon="Package" sortable sk="name">Plan</SortTh>
            <SortTh icon="Layers"  sortable sk="tier">Tier</SortTh>
            <SortTh icon="DollarSign" sortable sk="price">Pricing</SortTh>
            <Th icon="Key">Entitlements</Th>
            <Th icon="Sliders">Limits</Th>
            <SortTh icon="GitBranch" sortable sk="version">Version</SortTh>
            <SortTh icon="Users"    sortable sk="subs">Subscribers</SortTh>
            <Th icon="Activity">Status</Th>
            <Th end />
          </tr></thead>
          <tbody>
            {sorted.map(plan => {
              const fkCount = Array.isArray(plan.featureKeys) ? plan.featureKeys.length : 0;
              let fkBarColor = 'transparent';
              if (fkCount === TOTAL_FEATURE_KEYS) fkBarColor = '#0E8C5A';
              else if (fkCount > 0) fkBarColor = 'var(--nx-accent)';
              return (
                <tr key={plan.id} className="nx-tr" style={{ cursor: 'pointer' }} onClick={() => setViewing(plan)}>
                  <td>
                    <span className="nx-row-name">
                      <span className="nx-domain-mark" style={{ width: 28, height: 28, fontSize: 11, background: `var(--nx-${TIER_TONE[plan.tier]}-tint,#f1f5f9)`, color: `var(--nx-${TIER_TONE[plan.tier]},#64748B)` }}>
                        {plan.name[0]}
                      </span>
                      <div>
                        <span className="nx-row-name-text">{plan.name}</span>
                        {plan.isDefault && (
                          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, background: '#6E3FBA18', color: '#6E3FBA', borderRadius: 4, padding: '1px 5px' }}>DEFAULT</span>
                        )}
                        <div className="nx-muted nx-mini" style={{ fontFamily: 'monospace' }}>{plan.code}</div>
                      </div>
                    </span>
                  </td>
                  <td><Pill tone={TIER_TONE[plan.tier]}>{TIER_LABEL[plan.tier]}</Pill></td>
                  <td>
                    <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                      <div>{fmtPrice(plan.priceMonthly, plan.currency || 'INR')}<span className="nx-muted">/mo</span></div>
                      {plan.priceAnnual != null && <div>{fmtPrice(plan.priceAnnual, plan.currency || 'INR')}<span className="nx-muted">/yr</span></div>}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 40, height: 4, background: 'var(--nx-border)', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
                        <div style={{ height: '100%', width: (fkCount / TOTAL_FEATURE_KEYS * 100) + '%', background: fkBarColor, borderRadius: 2 }} />
                      </div>
                      <span style={{ fontSize: 12, fontFamily: 'monospace' }}>{fkCount}<span className="nx-muted">/{TOTAL_FEATURE_KEYS}</span></span>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                      <div><span className="nx-muted">WS: </span>{plan.maxWorkspaces ?? <span className="nx-muted">∞</span>}</div>
                      <div><span className="nx-muted">GB: </span>{plan.maxStorageGb != null ? `${plan.maxStorageGb} GB` : <span className="nx-muted">∞</span>}</div>
                    </div>
                  </td>
                  <td><span className="nx-cell is-mono" style={{ fontSize: 12 }}>v{plan.version}</span></td>
                  <td><span className="nx-cell is-mono">{plan._count?.subscriptions ?? 0}</span></td>
                  <td><Pill tone={STATUS_TONE[plan.status] || 'gray'}>{plan.status}</Pill></td>
                  <td className="nx-td-end" onClick={e => e.stopPropagation()}>
                    <ActionsMenu
                      plan={plan}
                      onView={() => setViewing(plan)}
                      onEdit={() => setEditing(plan)}
                      onClone={() => setCloning(plan)}
                      onSetDefault={() => setSettingDefault(plan)}
                      onArchive={() => setArchiving(plan)}
                      onRestore={() => setRestoring(plan)}
                      onHistory={() => setViewingHistory(plan)}
                      onCompare={() => { setCompareInit(plan.id); setComparing(true); }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div></div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <Btn variant="ghost" size="sm" icon="ChevronLeft" disabled={page <= 1} onClick={() => setPage(p => p - 1)} />
          <span style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
            Page {page} of {totalPages}
          </span>
          <Btn variant="ghost" size="sm" icon="ChevronRight" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} />
        </div>
      )}

      {/* Create / Edit forms */}
      {creating && (
        <PlanForm initial={EMPTY_FORM} onSave={onCreated} onClose={() => setCreating(false)} isEdit={false} />
      )}
      {editing && (
        <PlanForm
          initial={{
            ...editing,
            displayName:         editing.displayName  || '',
            featureKeys:         Array.isArray(editing.featureKeys) ? editing.featureKeys : [],
            isPublic:            editing.isPublic !== undefined ? editing.isPublic : true,
            status:              editing.status || 'ACTIVE',
            priceMonthly:        editing.priceMonthly ?? '',
            priceAnnual:         editing.priceAnnual  ?? '',
            currency:            editing.currency     || 'INR',
            trialDays:           editing.trialDays    ?? '14',
            maxWorkspaces:       editing.maxWorkspaces       ?? '',
            maxStorageGb:        editing.maxStorageGb        ?? '',
            maxAgents:           editing.maxAgents           ?? '',
            maxBots:             editing.maxBots             ?? '',
            maxApiCallsPerDay:   editing.maxApiCallsPerDay   ?? '',
            maxMessagesPerMonth: editing.maxMessagesPerMonth ?? '',
          }}
          onSave={onUpdated}
          onClose={() => setEditing(null)}
          isEdit={true}
        />
      )}

      {/* Clone */}
      {cloning && <CloneModal plan={cloning} onCloned={onCloned} onClose={() => setCloning(null)} />}

      {/* View */}
      {viewing && <ViewPlanModal plan={viewing} onClose={() => setViewing(null)} />}

      {/* Compare */}
      {comparing && <PlanCompareModal initialPlanId={compareInit} onClose={() => { setComparing(false); setCompareInit(null); }} />}

      {/* Archive confirm */}
      {archiving && (
        <ArchiveConfirmModal
          plan={archiving}
          onConfirm={doArchiveConfirm}
          onClose={() => setArchiving(null)}
          archiving={archivingBusy}
        />
      )}

      {/* Set default confirm */}
      {settingDefault && (
        <SetDefaultConfirmModal
          plan={settingDefault}
          currentDefault={defPlan}
          onConfirm={doSetDefaultConfirm}
          onClose={() => setSettingDefault(null)}
          saving={settingDefBusy}
        />
      )}

      {/* Restore confirm */}
      {restoring && (
        <RestoreConfirmModal
          plan={restoring}
          onConfirm={doRestoreConfirm}
          onClose={() => setRestoring(null)}
          restoring={restoringBusy}
        />
      )}

      {/* Version history drawer */}
      {viewingHistory && <VersionHistoryDrawer plan={viewingHistory} onClose={() => setViewingHistory(null)} />}
    </div>
  );
}
